import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Play, Pencil, Copy, Share2, Printer, Trash2, Coffee } from "lucide-react";
import { isEspresso, timeLabel } from "@/lib/brew";
import { shareRecipe, printRecipe } from "@/lib/share";
import { usePrefs } from "@/context/PrefsContext";
import { fmtWeight, fmtVolume } from "@/lib/units";

export default function Saved() {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { prefs } = usePrefs();

  const load = () => {
    api.get("/recipes").then(({ data }) => setRecipes(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const slugOf = (brewer) => brewer.toLowerCase().replace(/\s+/g, "-");

  const startBrew = (r) => {
    localStorage.setItem("bm_active_brew", JSON.stringify({ recipe: r, steps: r.instructions, total: r.duration, startTs: Date.now() }));
    navigate("/brew");
  };

  const duplicate = async (r) => {
    try { await api.post(`/recipes/${r.id}/duplicate`); toast.success("Recipe duplicated"); load(); }
    catch { toast.error("Could not duplicate"); }
  };

  const remove = async (r) => {
    try { await api.delete(`/recipes/${r.id}`); toast.success("Recipe deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="saved-page">
      <PageHeader title="Saved Recipes" onBack={() => navigate("/account")} />
      <div className="px-5 pt-5 space-y-3">
        {loading && <p className="text-[#EDE0CC]/60 text-sm">Loading…</p>}
        {!loading && recipes.length === 0 && (
          <div className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-6 text-center text-[#EDE0CC]/85">
            <Coffee size={30} className="mx-auto mb-3 text-[#EDE0CC]" />
            <p>No saved recipes yet.</p>
            <button
              onClick={() => navigate("/recipes")}
              className="mt-5 h-11 px-6 rounded-full bg-[#EDE0CC] text-[#081F5C] text-sm font-semibold active:scale-95 transition-transform"
              data-testid="empty-create-recipe-btn"
            >
              Create a recipe
            </button>
          </div>
        )}
        {recipes.map((r) => {
          const esp = isEspresso(r.brewer);
          return (
            <div key={r.id} className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4" data-testid={`saved-recipe-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-[#EDE0CC] truncate">{r.name}</div>
                  <div className="text-xs text-[#EDE0CC]/60 mt-0.5">
                    {r.brewer} · {fmtWeight(r.coffee, prefs)} {esp ? `→ ${fmtWeight(r.yield_g, prefs)}` : `/ ${fmtVolume(r.water, prefs)}`} · 1:{r.ratio} · {timeLabel(r.duration)}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full bg-[#EDE0CC]/10 text-[#EDE0CC]/70 shrink-0">
                  {r.mode === "guided" ? "Guided" : "Custom"}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                <IconBtn onClick={() => startBrew(r)} icon={Play} label="Brew" testid={`brew-${r.id}`} primary />
                <IconBtn onClick={() => navigate(`/brew-setup/${slugOf(r.brewer)}`, { state: { recipe: r } })} icon={Pencil} label="Edit" testid={`edit-${r.id}`} />
                <IconBtn onClick={() => duplicate(r)} icon={Copy} label="Copy" testid={`dup-${r.id}`} />
                <IconBtn onClick={() => shareRecipe(r)} icon={Share2} label="Share" testid={`share-${r.id}`} />
                <IconBtn onClick={() => printRecipe(r)} icon={Printer} label="Print" testid={`print-${r.id}`} />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-${r.id}`} className="flex items-center gap-1 h-9 px-3 rounded-full border border-[#E56B6B]/40 text-[#E56B6B] text-xs active:scale-95 transition-transform">
                      <Trash2 size={14} /> Delete
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0D2875] border-[#EDE0CC]/20 text-[#EDE0CC]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-[#EDE0CC]">Delete this recipe?</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#EDE0CC]/70">This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-full border-[#EDE0CC]/40 bg-transparent text-[#EDE0CC] hover:bg-[#EDE0CC]/10">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(r)} data-testid={`confirm-delete-${r.id}`} className="rounded-full bg-[#E56B6B] text-white hover:bg-[#d95a5a]">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
        <div className="h-4" />
      </div>
    </div>
  );
}

function IconBtn({ onClick, icon: Icon, label, testid, primary }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-1 h-9 px-3 rounded-full text-xs active:scale-95 transition-transform ${primary ? "bg-[#EDE0CC] text-[#081F5C] font-semibold" : "border border-[#EDE0CC]/30 text-[#EDE0CC]"}`}>
      <Icon size={14} /> {label}
    </button>
  );
}
